import TableLoader from '@/components/global/TableLoader'

import { onScroll, openPopup, pluralize } from '@/utils/helpers'
import { mapGetters, mapMutations } from 'vuex'

export default {
  components: { TableLoader },
  data () {
    return {
      list: [],
      popupDetails: null,
      searchTerm: null,
      filterOptions: {},
      webhooksAllowed: false,
      highlightSelectors: null,
      queryAction: null,
      shareQuery: false,
      paginateScrollFun: this.onScroll,
      paginate: {
        total: null,
        type: 'transaction',
        token_type: 'filter_token'
      },
      keys: {
        list: '',
        popup: null,
        defaultQuery: 'id'
      },
      params: {
        search: {},
        export: {},
        exportPath: null,
        status: null,
        bulkWebhooks: null,
        filtersLen: 1
      },
      func: {
        searchPrior: null,
        status: null,
        highlightCondition: null
      },
      api: {
        search: null,
        export: null,
        fetchStatus: null,
        resendWebhook: null,
        bulkResendWebhook: null
      }
    }
  },
  beforeDestroy () {
    this.SET_STATEMENT_FILTER({})
  },
  watch: {
    getterPopupName (newVal, oldVal) {
      if (oldVal === this.keys.popup && newVal === null) {
        this.$router.replace({query: {}})
      }
    }
  },
  computed: {
    ...mapGetters(['getterSearchValue', 'getterStatementFilter', 'getterPopupName']),
    filtersApplied () {
      return Object.keys(this.getterStatementFilter).length > this.params.filtersLen
    },
    emptyRecords () {
      if (this.filtersApplied) {
        return `No ${this.pluralize(this.paginate.type)} found. Please try changing the filters`
      }
      return `No ${this.pluralize(this.paginate.type)} found`
    }
  },
  methods: {
    onScroll,
    openPopup,
    pluralize,
    ...mapMutations(['SET_SECTION_DETAILS', 'SET_STATEMENT_FILTER']),
    startProcessing () {
      this.populateParams()

      if (this.paginate['token_type'] === 'next_page_token') {
        this.paginateScrollFun = this.quickPaginate
      }

      if (this.$route.query.hasOwnProperty('q')) {
        this.queryAction = 'Search'
      } else {
        this.populateData()
      }
    },
    quickPaginate (paginate, populateData, list) {
      if (this.list.length && !paginate['stop']) {
        let appearOnScroll = new IntersectionObserver((entries, appearOnScroll) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              populateData(true)
              appearOnScroll.unobserve(entry.target)
            }
          })
        })

        let tableRows = null
        if (list) {
          tableRows = document.querySelectorAll(list)
        } else {
          tableRows = document.querySelectorAll('.table-body .table-row')
        }

        let lastRow = tableRows.length - 1
        appearOnScroll.observe(tableRows[lastRow])
      }
    },
    populateData (start) {
      this.fetchRecords(start).then(() => {
        this.paginateScrollFun(this.paginate, this.populateData)
      })
    },
    setPaginate (data) {
      this.paginate['page_size'] = data.page_size
      this.paginate['data_added'] = true
      this.searchTerm = null

      if (this.paginate['token_type'] === 'next_page_token') {
        if (data.next_page_token) {
          this.paginate['next_page_token'] = data.next_page_token
          this.paginate['stop'] = false
        } else {
          this.paginate['stop'] = true
          this.paginate['total'] = 0
          this.paginate['next_page_token'] = null
        }
      } else {
        this.paginate['start'] = 0
        this.paginate['total'] = data.total
        this.paginate['filter_token'] = data.filter_token
      }
    },
    showDetails (details) {
      this.popupDetails = details
      this.openPopup(this.keys.popup)

      if (this.shareQuery) {
        this.$router.replace({query: {q: encodeURIComponent(details[this.keys.defaultQuery])}})
      }
    },
    resetData () {
      this.list = []
      this.paginate['data_added'] = false
      this.paginate['total'] = null
    },
    getListCount (resolve) {
      this.api.count({next_page_token: this.paginate['next_page_token']}).then(response => {
        let data = response.data
        if (data) {
          this.paginate['total'] = data.count
        }
        resolve()
      })
    },
    updateFilters (filters) {
      this.$router.replace({query: {}})
      this.filterOptions = filters || {}
      this.resetData()
      this.populateData()
      this.openPopup(null)
    },
    setSearchFunctions (fun) {
      this.shareQuery = true
      if (this.queryAction === 'Search') {
        const funs = fun()
        this.queryAction = null
        try {
          funs.updateQuery(decodeURIComponent(this.$route.query.q))
        } catch (e) {
          this.errorToastMsg('Invalid search param')
          this.populateData()
        }

        const errorMsg = funs.getError()
        if (errorMsg) {
          this.errorToastMsg(errorMsg)
          this.populateData()
        }
      }
    },
    triggerSearch (initial) {
      this.searchTerm = this.getterSearchValue.trim()
      if (this.func.searchPrior) {
        this.func.searchPrior()
      }

      if (this.searchTerm) {
        this.resetData()
        const params = {
          ...this.params.search,
          list_meta_needed: initial,
          q: this.searchTerm
        }

        this.api.search(params).then((response) => {
          let data = response.data
          if (data) {
            this.list = data[this.keys.list] || []
            this.paginate['total'] = this.list.length
            this.paginate['data_added'] = true

            if (this.paginate.token_type === 'next_page_token') {
              if (data.next_page_token) {
                this.paginate['next_page_token'] = data.next_page_token
                this.paginate['stop'] = true
              } else if (this.paginate.no_search_export) {
                this.paginate['stop'] = true
              }
            } else {
              this.paginate['filter_token'] = data.filter_token
            }

            this.postSearch(initial, this.paginate.total)
            if (initial) {
              this.populateMeta(response, true)
            }
          }
        }).catch(() => {
          this.list = []
          this.paginate['total'] = 0
          this.paginate['data_added'] = true
        })
      }
    },
    triggerSearchPaginate (start, initial) {
      let params = {}
      if (start) {
        if (this.paginate['token_type'] === 'filter_token') {
          params['offset'] = start
          params['filter_token'] = this.paginate.filter_token
        } else {
          params['next_page_token'] = this.paginate.next_page_token
        }
      } else {
        let search = this.getterSearchValue
        if (search.trim()) {
          this.searchTerm = search
          this.resetData()
        }
        params = {
          ...params,
          ...this.params.search,
          list_meta_needed: initial,
          q: search
        }
      }

      this.fetchSearchRecords(params, start, initial).then(() => {
        this.paginateScrollFun(this.paginate, this.triggerSearchPaginate)
      })
    },
    fetchSearchRecords (params, start, initial) {
      return new Promise(resolve => {
        this.api.search(params).then((response) => {
          let data = response.data
          if (data) {
            if (start) {
              this.list.push(...data[this.keys.list])
              this.postSearch(false, this.paginate.total)

              if (this.paginate['token_type'] === 'next_page_token') {
                this.checkPaginateV2(data.next_page_token)
              }
            } else {
              this.list = data[this.keys.list] || []
              this.paginate['start'] = 0
              this.paginate['total'] = data.total
              this.paginate['page_size'] = data.page_size
              this.paginate['data_added'] = true

              if (this.paginate['token_type'] === 'filter_token') {
                this.paginate['filter_token'] = data.filter_token
              } else {
                this.paginate['next_page_token'] = data.next_page_token
              }

              this.postSearch(initial, this.paginate.total)
              if (initial) {
                this.populateMeta(response, true)
              }
            }
            resolve()
          }
        }).catch(() => {
          this.list = []
          this.paginate['total'] = 0
        })
      })
    },
    postSearch (initial, txnLen) {
      if (initial && txnLen === 1 && this.keys.popup) {
        this.$nextTick(() => {
          this.showDetails(this.list[0])
        })
      }

      this.$nextTick(() => {
        this.highlightTerms()
      })
    },
    highlightTerms () {
      let searchCompare = val => {
        return val === this.searchTerm
      }
      if (this.func.highlightCondition) {
        searchCompare = val => {
          return this.func.highlightCondition(val)
        }
      }

      document.querySelectorAll(this.highlightSelectors).forEach(e => {
        if (searchCompare(e.innerText)) {
          e.classList.add('highlight')
        }
      })
    },
    checkPaginateV2 (nextPageToken) {
      if (nextPageToken) {
        this.paginate['next_page_token'] = nextPageToken
      } else {
        this.paginate['stop'] = true
      }
    },
    clearSearch () {
      this.$router.replace({query: {}})
      this.resetData()
      this.populateData()
    },
    setHighlightSelectors (indexes) {
      this.highlightSelectors = indexes.reduce((acc, val) => {
        acc.push(`.table-body .table-row p:nth-child(${val}) span`)
        return acc
      }, [])
      this.highlightSelectors = this.highlightSelectors.join(', ')
    },
    refreshDetails (event, {id}, fromDetailsPopup) {
      event.stopPropagation()

      this.api.fetchStatus(id, this.params.status).then(response => {
        let data = response.data
        if (data) {
          const rowIndex = this.list.findIndex(elem => elem.id === id)
          this.list.splice(rowIndex, 1, data)

          if (fromDetailsPopup) {
            this.showDetails(data)
          }
        } else if (response.reason === 'UNPRIVILEGED') {
          this.errorToastMsg(response.message)
        }
      })
    },
    setTokenInParams (params) {
      if (this.paginate.token_type === 'next_page_token') {
        params['next_page_token'] = this.paginate['next_page_token']
      } else {
        params['filter_token'] = this.paginate['filter_token']
      }
      return params
    },
    bulkResendWebhooks () {
      let params = this.setTokenInParams({})

      this.api.bulkResendWebhook(params, this.params.bulkWebhooks).then(response => {
        this.handleResponse(response)
      })
    },
    exportRecords () {
      let params = {
        ...this.params.export
      }
      params = this.setTokenInParams(params)

      this.api.export(params, this.params.exportPath).then((response) => {
        this.handleResponse(response)
      })
    },
    refreshList (txn) {
      const txnIndex = this.list.findIndex(elem => elem.id === txn.id)
      this.list.splice(txnIndex, 1, txn)
      this.showDetails(txn)
    },
    resendWebhook () {
      this.api.resendWebhook(this.popupDetails.id).then(response => {
        if (response.success) {
          this.successToastMsg(response.message)
        } else {
          this.errorToastMsg(response.message)
        }
      })
    },
    handleResponse (response) {
      if (response.success) {
        this.successToastMsg(response.message)
        this.openPopup(null)
      } else {
        this.errorToastMsg(response.message)
      }
    }
  }
}
